
















































































import { Component, Vue } from "vue-property-decorator";
import moment from "moment";
import Loading from "@/components/loader/loading.vue";
import StartSurvey from "@/views/survey/overview/StartSurvey.vue";
import { getDayEndISO, getDayStartISO, weekStamp } from "@/tool/beginAtEndAt";

interface Response {
  id: number;
  questionId: number;
  answerOptionId?: number;
  answerOptionIds?: number[];
  skipQuestion: boolean;
  createdAt: string;
  optionLabel?: string;
  selectedLabels?: string[];
  answerText?: string;
  ratingValue?: number;
}

interface Submission {
  submissionId: number;
  staffName: string | null;
  staffDepartment: string | null;
  staffTitle: string | null;
  createdAt: string;
  updatedAt: string | null;
  responses: Response[];
}

interface Question {
  id: number;
  questionType: string;
  questionText: string;
  title: string;
  subTitle: string | null;
  createdAt: string;
  options: Array<{
    id: number;
    questionId: number;
    label: string;
    value: number;
    order: number;
  }>;
}

interface Overview {
  id: number;
  companyId: number;
  questionId: number;
  slug: string;
  title: string;
  subTitle: string | null;
  questionType: string;
  createdAt: string;
  archivedAt: string | null;
}

interface TableItem {
  submissionId: number;
  answer: string;
  createdAt: string;
}

@Component({
  components: {
    Loading,
    StartSurvey,
  },
})
export default class StaffOverviewDetails extends Vue {
  loading = false;
  dispatchType = this.$route.query.dispatchType || "admission";

  overview: Overview | null = null;
  questions: Question[] = [];
  submissions: Submission[] = [];

  headers = [
    {
      text: "Submission ID",
      value: "submissionId",
      align: "center",
      width: "15%",
    },
    { text: "Staff Name", value: "staffName" },
    { text: "Department", value: "staffDepartment" },
    { text: "Title", value: "staffTitle" },
    {
      text: "Answer",
      value: "answer",
    },
    {
      text: "Submitted",
      value: "createdAt",
      align: "center",
      width: "20%",
    },
  ];

  async created() {
    const dispatchType = this.$route.query.dispatchType;
    if (dispatchType) {
      this.dispatchType = dispatchType.toString() || "staff-exit";
    }

    let beginAt = this.$route.query.beginAt;
    let endAt = this.$route.query.endAt;
    const overviewId = this.$route.query.overviewId;

    if (!beginAt && !endAt) {
      const { beginAt: newBeginAt, endAt: newEndAt } = weekStamp();
      beginAt = newBeginAt;
      endAt = newEndAt;
    } else {
      beginAt = getDayStartISO(beginAt as string);
      endAt = getDayEndISO(endAt as string);
    }

    const payload = {
      beginAt,
      endAt,
      slug: this.dispatchType,
      overviewId,
    };

    this.loading = true;
    try {
      await this.$store.dispatch(
        "staffSurvey/getStaffSurveySubmissionDetails",
        payload,
      );
      this.populateData();
    } catch (error) {
      console.error("Error loading survey data:", error);
    } finally {
      this.loading = false;
    }
  }

  populateData() {
    const data = this.$store.getters[
      "staffSurvey/staffSurveySubmissionDetails"
    ];

    if (data) {
      this.overview = data.overview || null;
      this.questions = data.questions || [];
      this.submissions = data.submissions || [];
    }
  }

  get question(): Question | undefined {
    return this.questions.length > 0 ? this.questions[0] : undefined;
  }

  get hasData(): boolean {
    return this.submissions && this.submissions.length > 0;
  }

  get tableItems(): TableItem[] {
    return this.submissions.map(submission => ({
      submissionId: submission.submissionId,
      staffName: submission.staffName || "N/A",
      staffDepartment: submission.staffDepartment || "N/A",
      staffTitle: submission.staffTitle || "N/A",
      answer: this.formatAnswer(submission),
      createdAt: submission.createdAt,
    }));
  }

  formatAnswer(submission: Submission): string {
    if (!submission.responses || submission.responses.length === 0) {
      return "No response";
    }

    const response = submission.responses[0];
    const questionType = this.question && this.question.questionType;

    // Handle single_choice questions
    if (questionType === "single_choice") {
      return response.optionLabel || "No response";
    }

    // Handle multi_choice questions (multiple selections)
    if (questionType === "multi_choice") {
      const labels = response.selectedLabels;
      if (Array.isArray(labels) && labels.length > 0) {
        return labels.join(", ");
      }
      return "No response";
    }

    // Handle yes_no questions
    if (questionType === "yes_no") {
      const optionId = response.answerOptionId;
      if (!optionId || !this.question) {
        return "No response";
      }

      const option = this.question.options.find(opt => opt.id === optionId);
      return option ? option.label : "No response";
    }

    // Handle text questions
    if (questionType === "text") {
      const text = response.answerText;
      return text && text.trim() ? text : "No response";
    }

    // Handle rating questions
    if (questionType === "rating") {
      const rating = response.ratingValue;
      if (rating !== undefined && rating !== null) {
        return rating.toString();
      }
      return "No response";
    }

    return "No response";
  }

  destroyed() {
    this.$store.dispatch("survey/clearSummaryDetails");
  }

  back() {
    this.$router.push({
      path: `/staff-survey/overview`,
      query: {
        dispatchType: this.dispatchType,
      },
    });
  }

  complete(dispatchType: string) {
    this.$router.push({
      path: `/staff-survey/overview`,
      query: {
        dispatchType: dispatchType,
      },
    });
  }

  formatDate(date: string): string {
    return moment(date).format("MM/DD/YYYY @ hh:mm");
  }
}
